import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useMemo, useRef, useState} from 'react'
import {NegativeFeedbackButton, PositiveFeedbackButton} from './Buttons'
import {FeedbackDetailsDialog} from './FeedbackDetailsDialog'
import {useHandleFeedbackSubmit, useSubmitFeedbackReason} from './hooks'
import {NegativeFeedbackReasonControls} from './NegativeFeedbackReasonControls'
import type {
  FeedbackOptions,
  FeedbackSubmitHandler,
  FeedbackType,
  NegativeFeedbackReasonsConfig,
  PostSubmitFeedbackDetailsConfig,
} from './types'
import {resolveFeedbackPath} from './utils'

export interface CopilotCodeReviewFeedbackProps {
  commentId: string
  commentUrl?: string
  feedbackPath?: string
  feedbackOptions?: FeedbackOptions | null
  additionalParameters?: Record<string, string>
  showNegativeFeedbackModal?: boolean
  postSubmitFeedbackDetails?: PostSubmitFeedbackDetailsConfig
  groupLabel?: string
  /**
   * Shows an opt-in "Add a reason" menu after 👎 instead of any negative-feedback dialog. When set, 👎 never opens
   * `NegativeFeedbackForm` or `postSubmitFeedbackDetails`. Only valid for `feedback_target: 'REVIEW_SUMMARY'`.
   */
  negativeFeedbackReasons?: NegativeFeedbackReasonsConfig
}

const Feedback: React.FC<CopilotCodeReviewFeedbackProps> = ({
  commentId,
  commentUrl,
  feedbackPath,
  feedbackOptions,
  additionalParameters,
  showNegativeFeedbackModal = true,
  postSubmitFeedbackDetails,
  groupLabel,
  negativeFeedbackReasons,
}) => {
  const [typeSubmitted, setTypeSubmitted] = useState<FeedbackType>()
  const [followupFeedbackType, setFollowupFeedbackType] = useState<FeedbackType>()
  const feedbackControlsRef = useRef<HTMLDivElement>(null)
  const reasonMode = negativeFeedbackReasons !== undefined

  const path = useMemo(() => resolveFeedbackPath({feedbackPath, commentUrl}), [commentUrl, feedbackPath])

  const handleSubmit = useHandleFeedbackSubmit({path, additionalParameters})
  const submitReason = useSubmitFeedbackReason({path, commentId, additionalParameters})

  const handleClick: FeedbackSubmitHandler = props => {
    setTypeSubmitted(props.type)
    handleSubmit(props)

    const reasonMenuHandlesType = reasonMode && props.type === 'NEGATIVE'
    if (!reasonMenuHandlesType && postSubmitFeedbackDetails?.feedbackTypes.includes(props.type)) {
      setFollowupFeedbackType(props.type)
    }
  }

  const handleDetailsSubmit: FeedbackSubmitHandler = props => {
    handleSubmit(props)
    setFollowupFeedbackType(undefined)
  }

  const detailsFeedbackOptions =
    postSubmitFeedbackDetails?.feedbackOptions === undefined
      ? feedbackOptions
      : postSubmitFeedbackDetails.feedbackOptions

  return (
    <div
      ref={feedbackControlsRef}
      data-testid={`copilot-code-review-feedback-${commentId}`}
      className="d-flex gap-1"
      role={groupLabel ? 'group' : undefined}
      aria-label={groupLabel}
      tabIndex={postSubmitFeedbackDetails ? -1 : undefined}
    >
      {postSubmitFeedbackDetails && followupFeedbackType && (
        <FeedbackDetailsDialog
          commentId={commentId}
          feedbackType={followupFeedbackType}
          detailsOnly={postSubmitFeedbackDetails.detailsOnly}
          feedbackOptions={detailsFeedbackOptions}
          feedbackChoiceLabel={postSubmitFeedbackDetails.feedbackChoiceLabel}
          title={postSubmitFeedbackDetails.title}
          subtitle={postSubmitFeedbackDetails.subtitle}
          textResponseLabel={postSubmitFeedbackDetails.textResponseLabel}
          allowTextOnly={postSubmitFeedbackDetails.allowTextOnly}
          returnFocusRef={feedbackControlsRef}
          onClose={() => setFollowupFeedbackType(undefined)}
          onSubmit={handleDetailsSubmit}
        />
      )}
      {(reasonMode || typeSubmitted !== 'NEGATIVE') && (
        <PositiveFeedbackButton
          disabled={!reasonMode && !!typeSubmitted}
          ariaDisabled={reasonMode && !!typeSubmitted}
          selected={typeSubmitted === 'POSITIVE'}
          commentId={commentId}
          onClick={handleClick}
        />
      )}
      {(reasonMode || typeSubmitted !== 'POSITIVE') &&
        (negativeFeedbackReasons && negativeFeedbackReasons.options.length > 0 && typeSubmitted === 'NEGATIVE' ? (
          <NegativeFeedbackReasonControls
            commentId={commentId}
            config={negativeFeedbackReasons}
            submitReason={submitReason}
          />
        ) : (
          <NegativeFeedbackButton
            disabled={!reasonMode && !!typeSubmitted}
            ariaDisabled={reasonMode && !!typeSubmitted}
            selected={typeSubmitted === 'NEGATIVE'}
            onClick={handleClick}
            commentId={commentId}
            feedbackOptions={feedbackOptions}
            showNegativeFeedbackModal={!reasonMode && showNegativeFeedbackModal}
          />
        ))}
    </div>
  )
}

export const CopilotCodeReviewFeedback: React.FC<CopilotCodeReviewFeedbackProps> = props => (
  <ErrorBoundary boundaryName="CopilotCodeReviewFeedback" fallback={null}>
    <Feedback {...props} />
  </ErrorBoundary>
)
